import React from 'react'
import { createPortal } from 'react-dom'
import Highlighter from 'react-highlight-words'

import {
  CommandIcon,
  CommandRow,
  ContextPrefix,
  EmptyState,
  Match,
  Panel,
  ResultsList,
  Scrim,
  SearchInput,
  SectionLabel,
} from 'features/project/features/command-palette/components/CommandPaletteView/command-palette-view.styles'
import type { PaletteCommand } from 'features/project/features/command-palette/types'

type Props = {
  query: string
  commands: PaletteCommand[]
  focusedIndex: number
  onQueryChange: (query: string) => void
  onInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void
  onSelect: (command: PaletteCommand, index: number) => void
  onDismiss: () => void
}

export const CommandPaletteView = ({
  query,
  commands,
  focusedIndex,
  onQueryChange,
  onInputKeyDown,
  onSelect,
  onDismiss,
}: Props): React.ReactElement => {
  const queryTokens = query.trim().split(/\s+/).filter(Boolean)

  return createPortal(
    <Scrim
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onDismiss()
      }}
    >
      <Panel role="dialog" aria-modal="true" aria-label="Command palette">
        <SearchInput
          autoFocus
          placeholder="Type a command or search…"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          onKeyDown={onInputKeyDown}
        />
        <ResultsList role="listbox">
          {commands.length === 0 && (
            <EmptyState>No matching commands</EmptyState>
          )}
          {commands.map((command, index) => {
            const Icon = command.icon
            const focused = index === focusedIndex
            return (
              <React.Fragment key={command.id}>
                {command.section !== commands[index - 1]?.section && (
                  <SectionLabel>{command.section}</SectionLabel>
                )}
                <CommandRow
                  type="button"
                  role="option"
                  aria-selected={focused}
                  $focused={focused}
                  ref={(element) => {
                    if (focused) element?.scrollIntoView({ block: 'nearest' })
                  }}
                  onClick={() => onSelect(command, index)}
                >
                  <CommandIcon>{Icon && <Icon />}</CommandIcon>
                  {command.context && command.context.length > 0 && (
                    <ContextPrefix>
                      {command.context.join(' › ')} ›
                    </ContextPrefix>
                  )}
                  <Highlighter
                    highlightTag={Match}
                    searchWords={queryTokens}
                    autoEscape
                    textToHighlight={command.label}
                  />
                </CommandRow>
              </React.Fragment>
            )
          })}
        </ResultsList>
      </Panel>
    </Scrim>,
    document.body,
  )
}
